import { Routing } from '../Routes';
import { Main } from '../Main/Main';
import { AccountPageNavigation } from '../AccountPageNavigation/AccountPageNavigation';
import { ContentCard } from '../ContentCard/ContentCard';
import './SpecialPricesPage.css';
import { coins, Icon } from '../Icon/Icon';
import { EventRow } from '../EventRow/EventRow';
import { HorizontalProductListing } from '../HorizontalProductListing/HorizontalProductListing';
import { FilterButton } from '../Pagination/FilterButton/FilterButton';
import { SortingChooser } from '../Pagination/SortingChooser/SortingChooser';
import { useTranslation } from '../../i18n/i18n';
import { ApiProduct, ApiSpecialsProduct, ApiUser } from '../../types';
import { GenerateSpecialPriceModal } from '../specialPrices/GenerateSpecialPriceModal/GenerateSpecialPriceModal';
import { useSession } from '../../state/session';
import { LoaderPage } from './LoaderPage';
import { ErrorPage } from './ErrorPage';
import { UiContainer } from '../../state/ui';
import { SpecialPricesProductGrid } from '../specialPrices/SpecialPricesProductGrid';
import { IndexedSpecialPrices, ProductContainer } from '../../state/product';
import { isEmpty } from '../utils';
import { Res, useApi } from '../../state/api';
import { HeaderCard } from '../HeaderCard/HeaderCard';
import { useState } from 'react';

export const SpecialPricesPage = (): JSX.Element => {
	const { specialsProducts, specialProductsError, specialProducsIsLoading, user } = useSession();
	const ui = UiContainer.useContainer();
	const { isSpecialPricesModalOpen } = ui;
	const { specialPricesIndex } = ProductContainer.useContainer();
	const t = useTranslation().specialPrices;
	const [expiredProductId, setExpiredProductId] = useState<number | undefined>(undefined);

	if (!user) return <></>;
	if (specialProducsIsLoading) return <LoaderPage />;
	if (specialProductsError) return <ErrorPage />;

	const filteredSpecialsProducts = removeSpecialsProducts(specialsProducts, specialPricesIndex);

	const handleAddProduct = (expiredProductId?: number) => {
		if (typeof expiredProductId === 'number') {
			setExpiredProductId(expiredProductId);
			ui.setSpecialPricesModalIsOpen(true);
		} else {
			ui.setSpecialPricesModalIsOpen(true);
		}
	};

	const setIsModalOpen = (open: boolean) => {
		ui.setSpecialPricesModalIsOpen(open);
	};

	const getTotalSpecialSlots = (apiUser: ApiUser | boolean | undefined) => {
		if (typeof apiUser === 'object') {
			return apiUser.specialSlots;
		}

		return 0;
	};

	const totaltSpecialSlots = getTotalSpecialSlots(user);
	const selectedSpecialProducts = getSelectedSpecialPriceProducts(specialPricesIndex);

	return (
		<Main>
			<AccountPageNavigation />
			<div id="special-prices-page">
				{totaltSpecialSlots > 0 && (
					<>
						<HeaderCard
							heading={t.mySpecialPrices}
							subheading={t.choose4FavoriteProducts}
							listItems={[
								t.chooseFromAllProducts,
								t.generatePriceSpecificallyForYou,
								t.lockPriceFor6Months,
							]}
							bannerImage="/images/special-prices-header-image.png"
						/>
						<h2 className="heading">{t.productsChoosenByMe}</h2>
						<SpecialPricesProductGrid
							selectedSpecialPriceProducts={selectedSpecialProducts}
							totalSlots={totaltSpecialSlots}
							handleAddProduct={handleAddProduct}
						/>
						<SpecialPricesEvents />
						<BonusCard />
					</>
				)}
				<SpecialPriceProductListing selectedProducts={filteredSpecialsProducts} slots={totaltSpecialSlots} />
				<GenerateSpecialPriceModal
					open={isSpecialPricesModalOpen}
					setOpen={setIsModalOpen}
					expiredProductId={expiredProductId}
					onClose={() => setExpiredProductId(undefined)}
				/>
			</div>
		</Main>
	);
};

export const specialPricesRouting: Routing = {
	path(urls) {
		return urls.specialPrices;
	},
	component: SpecialPricesPage,
	requiresAuth: true,
};

export const specialPricesRouting2: Routing = {
	path(urls) {
		return urls.specialPrices2;
	},
	component: SpecialPricesPage,
	requiresAuth: true,
};

const SpecialPricesEvents = () => {
	const t = useTranslation().specialPrices;
	const { data } = useApi(Res.specialsPriceEvents, {});
	if (!data || data.length === 0) return;
	return (
		<div className="events">
			<h2 className="heading">{t.events}</h2>
			{data.map((specialsMessage) => (
				<EventRow
					id={specialsMessage.id}
					title={specialsMessage.title}
					message={specialsMessage.message}
					type={specialsMessage.type}
					key={specialsMessage.id}
				/>
			))}
		</div>
	);
};

const SpecialPriceProductListing = ({
	selectedProducts,
	slots,
}: {
	selectedProducts: ApiSpecialsProduct[];
	slots: number;
}) => {
	const urls = useTranslation().routes;
	const t = useTranslation().specialPrices;

	if (selectedProducts.length === 0 && slots > 0) return <></>;
	if (selectedProducts.length === 0)
		return (
			<>
				<h1 className="private-page-heading">{t.specialPrices}</h1>
				<p>{t.noSpecialPrices}</p>
			</>
		);

	return (
		<div className="selected-by-us">
			<div className="heading-filter-section">
				<h2 className="listing-heading">{slots > 0 ? t.productsChoosenByUs : t.mySpecialPrices}</h2>
				<div style={{ display: 'none' }}>
					<div className="filter-wrapper desktop">
						<FilterButton />
						<SortingChooser perma={urls.specialPrices} />
					</div>
					<div className="mobile-buttons">
						<div className="filter-wrapper">
							<FilterButton />
							<SortingChooser perma={urls.specialPrices} />
						</div>
					</div>
				</div>
			</div>
			{/* 			<div className="spacing" /> */}
			<HorizontalProductListing products={selectedProducts.map((sp) => sp.product)} />
		</div>
	);
};

const BonusCard = () => {
	const t = useTranslation().specialPrices;
	return (
		<div className="bonus">
			<div className="content-card bonus">
				<div className="left-col">
					<ContentCard>
						<h2>{t.countrysLargestSortiment}</h2>
						<div className="bonus-icon">
							<Icon shape={coins} />
						</div>
					</ContentCard>
				</div>
				<div className="right-col">
					<ContentCard>
						<p>{t.weOfferExtra}</p>
						<p>{t.noNegotiations}</p>
						<p>{t.inReasonableTime}</p>
					</ContentCard>
				</div>
			</div>
		</div>
	);
};

export const removeSpecialsProducts = (
	specialPriceProducts: ApiProduct[],
	IndexedSpecialPrices: IndexedSpecialPrices
) => {
	return specialPriceProducts.map((p) => IndexedSpecialPrices[p.id]).filter((p) => !p.customerSpecial);
};

export const getSelectedSpecialPriceProducts = (indexedSpecialPricesProducts: IndexedSpecialPrices): ApiProduct[] => {
	if (isEmpty(indexedSpecialPricesProducts)) return [];

	return Object.values(indexedSpecialPricesProducts)
		.filter((specialProduct: ApiSpecialsProduct) => specialProduct.customerSpecial)
		.sort((a, b) => new Date(a.lockedUntil).getTime() - new Date(b.lockedUntil).getTime())
		.map((specialProduct: ApiSpecialsProduct) => specialProduct.product);
};
